import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useConnection } from '../connection';
import { resolvePersona } from '../personas';
import { readValue, writeValue } from '../storage';
import { getLocalDateString } from '../teatime';
import { getInbox, inboxQueryKey } from './announcements';
import { faqsQueryKey, getFaqs } from './faqs';
import { getTodayPicks, teatimePicksQueryKey } from './picks';
import { usePartner, useUser } from './useUser';

/**
 * Pre-cache today's essentials — Phase 6 Offline-First sub-commit 2
 * (13 Jun 2026), extended for FAQs in sub-commit 6a (13 Jun 2026).
 *
 * Eagerly populate the runtime caches (TanStack Query in-memory + the
 * SW's `ti-api-v5` IDB cache from sub-commit 1) with the GETs a guest
 * is most likely to need offline later:
 *
 *   1. GET /v1/announcements          — guest inbox list
 *   2. GET /v1/picks/teatime?date=... — today's tea-time picks
 *   3. GET /v1/faqs                   — partner FAQ catalogue (6a)
 *
 * All three endpoints are normally first-touched on demand: inbox on
 * tap of the bell icon, picks at the 16:00 teatime nudge fire, FAQs
 * at first offline send (the offline AI fallback in sub-commit 6b
 * runs the fuzzy-matcher against this data). Without pre-cache, a
 * guest reopening the PWA tomorrow morning on no signal sees a broken
 * inbox, a failed teatime nudge, AND an offline fallback that can't
 * surface saved answers because the catalogue isn't in cache. With
 * pre-cache (today, while still online), the SW serves yesterday's
 * cached data instantly — the premium offline-first promise.
 *
 * Gates (all must pass before any prefetch fires):
 *
 *   - Signed in with a resolved Clerk userId. Pre-Clerk-load is a no-op.
 *   - Onboarding complete (`user.profile` present). Pre-onboarding
 *     guests have nothing to fetch — picks endpoint requires the
 *     server-resolved town, FAQs endpoint requires the resolved
 *     partner attribution, and both require a `users` row with a
 *     profile.
 *   - Persona resolves. Catches the rare case where a partner
 *     attribution is in flux and `resolvePersona` returns null.
 *   - Connection healthy (`useConnection().shouldSend === true`). The
 *     whole point of pre-cache is "do this while we have signal";
 *     prefetching offline would just write empty error responses to
 *     the cache layer.
 *   - Once-per-day per-user. The localStorage flag
 *     `ti:prefetched:<userId>:<dateLocal>` guards against re-prefetching
 *     when a guest reopens the PWA mid-day. Day rollover triggers a
 *     fresh prefetch via the new dateLocal segment.
 *
 * Fire-and-forget: never awaits. The flag is written BEFORE the
 * prefetch promises resolve, deliberately, to prevent retry storms
 * on persistent server-side failure. The cost of "we skip prefetch
 * for the rest of today after a transient failure" is one day of
 * slightly colder offline cache — the application-layer queries
 * (`useInbox` on /inbox visit, `fetchQuery` at teatime, `useFaqs`
 * on the offline-send branch in MePage) will still fetch on demand
 * later, and tomorrow's mount tries pre-cache again naturally when
 * the dateLocal segment changes.
 *
 * What's NOT prefetched here:
 *   - GET /v1/users/me — already fetched by MePage's useUser() on mount.
 *   - GET /v1/chat/history — already fetched by useChatHistory() and
 *     persisted via the TanStack Query IDB persister (main.tsx
 *     dehydrate filter explicitly allowlists chat history).
 *   - GET /v1/announcements/unread-count — already fetched by
 *     useUnreadCount() on mount.
 *   - Weather / tide times — no `/v1/weather` or `/v1/tides` route
 *     exists. Likely sourced from a 3rd-party origin or via LLM tool
 *     calls. Revisit when those routes land (would require either
 *     adding the 3rd-party origin to the SW's API_ORIGIN list, or
 *     proxying via a new Worker route).
 *   - Events catalogue — no `/v1/events` route mounted; events ride
 *     on the picks catalogue today.
 *   - Businesses catalogue — global-read; only reached via LLM tool
 *     calls today. Revisit if a direct endpoint lands.
 *
 * Cache interaction (sub-commit 1):
 *   prefetchQuery → fetcher → fetch() → SW interception. The SW caches
 *   the 2xx response in `ti-api-v5` under a user-scoped key. The
 *   TanStack Query in-memory cache holds the parsed data. Two layers,
 *   one fetch — no duplication, both populated.
 */

const PREFETCH_FLAG_PREFIX = 'prefetched';

const prefetchFlagSchema = z.object({
  at: z.string().datetime(),
});

function prefetchFlagKey(userId: string, dateLocal: string): string {
  return `${PREFETCH_FLAG_PREFIX}:${userId}:${dateLocal}`;
}

export function usePrefetchEssentials(): void {
  const { isSignedIn, userId, getToken } = useAuth();
  const { data: user } = useUser();
  const { data: partner } = usePartner();
  const { shouldSend } = useConnection();
  const queryClient = useQueryClient();
  const firedRef = useRef(false);

  useEffect(() => {
    // Per-mount idempotency. Survives re-renders (TanStack data refs
    // changing, partner resolving after user, etc.) within the same
    // MePage lifetime. Sign-out unmounts MePage, which resets the ref
    // — next sign-in starts fresh, but is bounded by the localStorage
    // flag below for the cross-session case.
    if (firedRef.current) return;

    if (!isSignedIn || !userId) return;
    if (!user?.profile) return;
    if (!shouldSend) return;

    const persona = resolvePersona(user.townSlug, partner ?? null);
    if (!persona) return;

    const dateLocal = getLocalDateString();
    const flagName = prefetchFlagKey(userId, dateLocal);
    if (readValue(flagName, prefetchFlagSchema) !== null) {
      // Already prefetched today (different session, earlier today).
      // Pin firedRef so we don't re-check storage on every re-render.
      firedRef.current = true;
      return;
    }

    // Set the flag BEFORE firing. A persistent server outage would
    // otherwise re-fire the effect on every queryClient/getToken ref
    // change, hammering a degraded API. One day of cold offline cache
    // after a transient failure is the right trade.
    writeValue(flagName, { at: new Date().toISOString() });
    firedRef.current = true;

    // Fire-and-forget. prefetchQuery resolves on success OR error;
    // we don't act on either. The SW cache layer (sub-commit 1)
    // captures the 2xx response naturally on the underlying fetch.
    void queryClient.prefetchQuery({
      queryKey: inboxQueryKey(userId),
      queryFn: () => getInbox({ getToken }),
      staleTime: 30 * 1000,
    });

    void queryClient.prefetchQuery({
      queryKey: teatimePicksQueryKey(persona.slug, dateLocal),
      queryFn: () => getTodayPicks({ date: dateLocal, getToken }),
      staleTime: 4 * 60 * 60 * 1000,
    });

    // FAQs (sub-commit 6a). 1h staleTime matches the useFaqs hook;
    // partner-curated FAQs change infrequently so the in-memory
    // cache stays "fresh" from the hook's POV well past the typical
    // session length, avoiding redundant refetches on every consumer
    // mount. The SW cache (sub-commit 1) holds the response for the
    // offline fuzzy-matcher in sub-commit 6b.
    void queryClient.prefetchQuery({
      queryKey: faqsQueryKey(userId),
      queryFn: () => getFaqs({ getToken }),
      staleTime: 60 * 60 * 1000,
    });
  }, [
    isSignedIn,
    userId,
    user,
    partner,
    shouldSend,
    queryClient,
    getToken,
  ]);
}
